import EmailIcon from "@mui/icons-material/Email";
import KeyIcon from "@mui/icons-material/Key";
import { DialogActions, DialogContent } from "@mui/material";
import {
  Autocomplete,
  Button,
  CreatedUpdatedBy,
  DateTimePicker,
  Field,
  FieldsContainer,
  Form,
  PageHeader,
  Spacer,
  TextField,
} from "common";
import { ParamsContext } from "common/components/Form/ParamsContext";
import FilterProvider from "components/FilterProvider";
import BookingList from "features/bookings/BookingList";
import HistoryListDialog from "features/histories/HistoryListDialog";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { createContext } from "react";
import useReviewEmailSentForm, {
  UseReviewEmailSentFormProps,
  UseReviewEmailSentFormReturnType,
} from "./useReviewEmailSentForm";

export const ReviewEmailSentContext = createContext<
  UseReviewEmailSentFormReturnType | undefined
>(undefined);

export interface ReviewEmailSentFormProps extends UseReviewEmailSentFormProps {}

export default function ReviewEmailSentForm({
  id,
  afterSave,
  onClose,
  defaultValues,
}: ReviewEmailSentFormProps) {
  const { hasPermissions, canEditItem } = useAccess();
  const form = useReviewEmailSentForm({
    id,
    afterSave,
    defaultValues,
  });
  const {
    params,
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
  } = form;
  const title = isCreate
    ? I18n.t("ReviewEmailSent.addTitle")
    : `${I18n.t("ReviewEmailSent.title")} #${item.id}`;
  const subtitle = isCreate ? undefined : item.email;
  return (
    <ParamsContext.Provider value={{ review_email_sent_id: id }}>
      <ReviewEmailSentContext.Provider value={form}>
        <PageHeader
          avatar={<EmailIcon />}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          loading={loading}
        />
        <DialogContent>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="ReviewEmailSent.attributes"
            translationPlaceholdersCategory="ReviewEmailSent.placeholders"
            translationHelpersCategory="ReviewEmailSent.helpers"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
            disabled={!canEditItem("review-email-sent", item)}
          >
            <FieldsContainer>
              <Field>
                <Autocomplete
                  id="booking_id"
                  label={I18n.t("ReviewEmailSent.attributes.booking_id")}
                />
              </Field>
              <Field>
                <TextField id="email" type="email" />
              </Field>
              <Field>
                <DateTimePicker id="sent_at" />
              </Field>
              <Field>
                <TextField id="template_used" />
              </Field>
            </FieldsContainer>
          </Form>
          {isUpdate && (
            <>
              <Spacer h={3} />
              {hasPermissions(["booking.viewany", "booking.viewown"]) && (
                <FilterProvider
                  id="review-email-sent.bookings"
                  storageType="session"
                >
                  <BookingList
                    id="review-email-sent.booking-list"
                    icon={<KeyIcon />}
                    title={I18n.t("Booking.title")}
                    showHeader={false}
                    modalProps={{
                      maxWidth: "lg",
                    }}
                  />
                </FilterProvider>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {isUpdate && (
            <CreatedUpdatedBy
              objectName="ReviewEmailSent"
              item={item}
              sx={{
                p: 1,
                paddingTop: 0,
                paddingBottom: 0.5,
                marginRight: "auto",
              }}
              renderDialog={
                hasPermissions(["history.viewany", "history.viewown"])
                  ? props => <HistoryListDialog {...props} subtitle={title} />
                  : undefined
              }
            />
          )}
          <Button
            title={I18n.t("App.save")}
            disabled={!isDirty}
            onClick={() => save()}
            loading={saving}
          />
        </DialogActions>
      </ReviewEmailSentContext.Provider>
    </ParamsContext.Provider>
  );
}
